import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dropdown,
  Input,
  MenuProps,
  Popover,
  Skeleton,
  Switch,
  Tag,
} from 'antd';
import {
  PlusIcon,
  Info,
  EllipsisVerticalIcon,
  ArrowRightIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useForm, useWatch } from 'react-hook-form';
import { AnimatePresence } from 'framer-motion';
import {
  DEFAULT_MASK_PATTERNS,
  IDataMasking,
  IDataMaskingTableColumns,
  IGetDataMaskingParams,
  MASK_TYPE_OPTIONS,
  MaskType,
  STATUS_OPTIONS,
} from '../../../pages/TravelRequest/constants/secureMasking.interface';
import { SystemProcessHomeAction } from '../../../enums';
import { useNotification } from '../../../contexts';
import {
  useGetMaskingDataQuery,
  useCreateMaskingDataMutation,
  useUpdateMaskingDataMutation,
  useGetTablesQuery,
  useGetTableColumnsQuery,
} from '../../../store';
import { formatDayJsDate } from '../../../utils';
import {
  ButtonWizard,
  HeadingWizard,
  SelectInput,
  TextInput,
} from '../..';
import CommonTravelTable from './CommonTravelTable';
import { CardWizard, SideSheet, SideSheetHeader } from '../../../layouts';

const columnHelper = createColumnHelper<IDataMaskingTableColumns>();

// Menu items for action dropdown
const MASKING_ACTION_MENU = [
  {
    key: SystemProcessHomeAction.EDIT,
    label: 'Edit',
  },
];

// Form interface
interface IMaskingForm {
  id?: number;
  tableName: string | null;
  columnName: string | null;
  maskType: MaskType | string | null;
  maskChar: string;
  maskPattern: string;
  status: number | null;
}

/**
 * Parses mask pattern into human-readable description
 * S{n} = Show first n characters
 * S{-n} = Hide last n characters (show all except last n)
 * E{n} = Show last n characters
 * E{-n} = Hide first n characters (show all except first n)
 * * = Mask characters in between
 * @domain = Preserve domain in email
 */
const parseMaskPattern = (pattern: string | null | undefined): string => {
  if (!pattern) return '-';

  const descriptions: string[] = [];

  // Check for S{n} or S{-n} - Start pattern
  const startMatch = pattern.match(/S\{(-?\d+)\}/);
  if (startMatch) {
    const num = parseInt(startMatch[1], 10);
    if (num >= 0) {
      descriptions.push(`Show first ${num} chars`);
    } else {
      descriptions.push(`Hide last ${Math.abs(num)} chars`);
    }
  }

  // Check for E{n} or E{-n} - End pattern
  const endMatch = pattern.match(/E\{(-?\d+)\}/);
  if (endMatch) {
    const num = parseInt(endMatch[1], 10);
    if (num >= 0) {
      descriptions.push(`Show last ${num} chars`);
    } else {
      descriptions.push(`Hide first ${Math.abs(num)} chars`);
    }
  }

  // Check for @domain - Preserve domain
  if (pattern.includes('@domain')) {
    descriptions.push('Preserve domain');
  }

  // Check for * - Mask middle
  if (pattern.includes('*')) {
    const hasPositiveStart = startMatch && parseInt(startMatch[1], 10) >= 0;
    const hasPositiveEnd = endMatch && parseInt(endMatch[1], 10) >= 0;

    if (!hasPositiveStart && !hasPositiveEnd) {
      descriptions.push('Mask all');
    } else {
      descriptions.push('Mask middle');
    }
  }

  if (descriptions.length === 0) {
    return pattern; // Return raw pattern if can't parse
  }

  return descriptions.join(', ');
};

/**
 * Get example based on mask pattern and type
 */
const getMaskExample = (
  pattern: string | null | undefined,
  maskType: string,
  maskChar = '*',
): string => {
  if (!pattern) return '';

  const char = maskChar || '*';

  // Parse the pattern to get start and end values
  const startMatch = pattern.match(/S\{(-?\d+)\}/);
  const endMatch = pattern.match(/E\{(-?\d+)\}/);

  const startNum = startMatch ? parseInt(startMatch[1], 10) : null;
  const endNum = endMatch ? parseInt(endMatch[1], 10) : null;

  // Handle S{-n} pattern (hide last n chars)
  if (startNum !== null && startNum < 0) {
    const hideCount = Math.abs(startNum);
    switch (maskType) {
      case 'phone':
        return `987654${char.repeat(hideCount)}`;
      case 'email':
        return `john.doe${char.repeat(hideCount)}@gmail.com`;
      default:
        return `ABCDEF${char.repeat(hideCount)}`;
    }
  }

  // Handle E{-n} pattern (hide first n chars)
  if (endNum !== null && endNum < 0) {
    const hideCount = Math.abs(endNum);
    switch (maskType) {
      case 'phone':
        return `${char.repeat(hideCount)}543210`;
      default:
        return `${char.repeat(hideCount)}GHIJKL`;
    }
  }

  // Default examples for positive patterns
  switch (maskType) {
    case 'email':
      if (pattern.includes('@domain')) {
        return `jo${char}${char}${char}@gmail.com`;
      }
      return `jo${char}${char}${char}@${char}${char}${char}.com`;
    case 'phone':
      return `98${char}${char}${char}${char}${char}${char}12`;
    case 'gstin':
      return `27${char}${char}${char}${char}${char}${char}${char}${char}${char}ABC`;
    case 'partial':
      return `AB${char}${char}${char}${char}XY`;
    default:
      return `XX${char}${char}${char}${char}YY`;
  }
};

const SecureMaskingDashboard = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // Pagination state
  const [pagination, setPagination] = useState<IGetDataMaskingParams>({
    page: 1,
    pageSize: 25,
    status: undefined,
    tableName: undefined,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState('');

  // Side sheet states
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form
  const maskForm = useForm<IMaskingForm>({
    defaultValues: {
      tableName: null,
      columnName: null,
      maskType: null,
      maskChar: '*',
      maskPattern: '',
      status: 1,
    },
  });

  // Watch tableName for column dropdown enable/disable
  const watchedTableName = useWatch({
    control: maskForm.control,
    name: 'tableName',
  });

  // Watch maskType for auto-fill pattern
  const watchedMaskType = useWatch({
    control: maskForm.control,
    name: 'maskType',
  });

  // Clear columnName when tableName changes (only in add mode)
  useEffect(() => {
    if (!isEditMode && watchedTableName) {
      maskForm.setValue('columnName', null);
    }
  }, [watchedTableName, isEditMode, maskForm]);

  // Auto-fill maskPattern when maskType changes (only in add mode)
  useEffect(() => {
    if (!isEditMode && watchedMaskType) {
      const defaultPattern = DEFAULT_MASK_PATTERNS[watchedMaskType] || '';
      maskForm.setValue('maskPattern', defaultPattern);
    }
  }, [watchedMaskType, isEditMode, maskForm]);

  // API Query & Mutation
  const maskingDataResponse = useGetMaskingDataQuery(pagination);
  const [createMaskingData, createMaskingResponse] =
    useCreateMaskingDataMutation();
  const [updateMaskingData, updateMaskingResponse] =
    useUpdateMaskingDataMutation();
  const { data: tablesResponse } = useGetTablesQuery();
  const { data: tableColumnsResponse } = useGetTableColumnsQuery(
    { tableName: watchedTableName },
    { skip: !watchedTableName },
  );
  // Handle open side sheet for Add
  const handleOpenAddSideSheet = useCallback(() => {
    setIsEditMode(false);
    setEditingId(null);
    maskForm.reset({
      tableName: null,
      columnName: null,
      maskType: null,
      maskChar: '*',
      maskPattern: '',
      status: 1,
    });
    setIsSideSheetOpen(true);
  }, [maskForm]);

  // Handle open side sheet for Edit
  const handleOpenEditSideSheet = useCallback(
    (rowData: IDataMasking) => {
      setIsEditMode(true);
      setEditingId(rowData.id);
      maskForm.reset({
        tableName: rowData.tableName,
        columnName: rowData.columnName,
        maskType: rowData.maskType,
        maskChar: rowData.maskChar || '*',
        maskPattern: rowData.maskPattern || '',
        status: rowData.status,
      });
      setIsSideSheetOpen(true);
    },
    [maskForm],
  );

  // Handle close side sheet
  const handleCloseSideSheet = useCallback(() => {
    setIsSideSheetOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    maskForm.reset({
      tableName: null,
      columnName: null,
      maskType: null,
      maskChar: '*',
      maskPattern: '',
      status: 1,
    });
  }, [maskForm]);

  // Handle form submit
  const handleFormSubmit = useCallback(
    async (data: IMaskingForm) => {
      try {
        if (isEditMode && editingId) {
          // Update existing
          await updateMaskingData({
            id: editingId,
            body: {
              tableName: data.tableName,
              columnName: data.columnName,
              maskType: data.maskType as MaskType,
              maskChar: data.maskChar,
              maskPattern: data.maskPattern || undefined,
              status: data.status,
            },
          }).unwrap();

          showNotification(
            'success',
            'Success',
            'Configuration updated successfully',
          );
        } else {
          // Create new
          await createMaskingData({
            tableName: data.tableName,
            columnName: data.columnName,
            maskType: data.maskType as MaskType,
            maskChar: data.maskChar,
            maskPattern: data.maskPattern || undefined,
            status: data.status,
          }).unwrap();

          showNotification(
            'success',
            'Success',
            'Configuration created successfully',
          );
        }

        handleCloseSideSheet();
        maskingDataResponse.refetch();
      } catch (error) {
        console.error('Error saving configuration:', error);
        showNotification(
          'error',
          'Error',
          `Failed to ${isEditMode ? 'update' : 'create'} configuration. Please try again.`,
        );
      }
    },
    [
      isEditMode,
      editingId,
      updateMaskingData,
      createMaskingData,
      showNotification,
      handleCloseSideSheet,
      maskingDataResponse,
    ],
  );

  // Handle status toggle
  const handleStatusToggle = useCallback(
    async (id: number, currentStatus: number) => {
      try {
        const newStatus = currentStatus === 1 ? 0 : 1;
        await updateMaskingData({
          id,
          body: { status: newStatus },
        }).unwrap();

        showNotification(
          'success',
          'Success',
          `Configuration ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`,
        );

        maskingDataResponse.refetch();
      } catch (error) {
        console.error('Error updating status:', error);
        showNotification(
          'error',
          'Error',
          'Failed to update status. Please try again.',
        );
      }
    },
    [updateMaskingData, showNotification, maskingDataResponse],
  );

  // Update total count on first page load
  useEffect(() => {
    if (
      pagination.page === 1 &&
      maskingDataResponse?.data?.count !== undefined
    ) {
      setTotalCount(maskingDataResponse.data.count);
    }
  }, [maskingDataResponse.data?.count, pagination.page]);

  // Search handler with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchText) {
        setPagination(prev => ({ ...prev, tableName: searchText, page: 1 }));
      } else {
        setPagination(prev => ({ ...prev, tableName: undefined, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  // Table columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('action', {
        header: () => '',
        cell: ({ row }) => {
          const rowData = row.original;

          const handleMenuClick: MenuProps['onClick'] = e => {
            switch (e.key) {
              case SystemProcessHomeAction.EDIT:
                handleOpenEditSideSheet(rowData);
                break;
              default:
                break;
            }
          };

          return (
            <Dropdown
              menu={{
                items: MASKING_ACTION_MENU,
                onClick: handleMenuClick,
              }}
            >
              <EllipsisVerticalIcon className="size-4 cursor-pointer" />
            </Dropdown>
          );
        },
        size: 48,
      }),
      columnHelper.accessor('status', {
        cell: info => {
          const status = info.getValue();
          const rowData = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={status === 1}
                onChange={() => handleStatusToggle(rowData.id, status)}
                loading={updateMaskingResponse.isLoading}
                size="small"
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
              <span
                className={`text-xs ${status === 1 ? 'text-green-600' : 'text-red-500'}`}
              >
                {status === 1 ? 'Active' : 'Inactive'}
              </span>
            </div>
          );
        },
        header: () => <span>Status</span>,
        size: 130,
      }),
      columnHelper.accessor('tableName', {
        cell: info => <span className="font-semibold">{info.getValue()}</span>,
        header: () => <span>Table Name</span>,
        size: 150,
      }),
      columnHelper.accessor('columnName', {
        cell: info => info.getValue(),
        header: () => <span>Column Name</span>,
        size: 150,
      }),
      columnHelper.accessor('maskType', {
        cell: info => {
          const maskType = info.getValue();
          const colorMap: Record<string, string> = {
            email: 'blue',
            phone: 'green',
            gstin: 'orange',
            partial: 'purple',
            custom: 'cyan',
          };
          return (
            <Tag color={colorMap[maskType] || 'default'}>
              {maskType?.toUpperCase()}
            </Tag>
          );
        },
        header: () => <span>Mask Type</span>,
        size: 120,
      }),
      columnHelper.accessor('maskChar', {
        cell: info => (
          <span className="font-mono bg-gray-100 px-2 py-1 rounded">
            {info.getValue() || '*'}
          </span>
        ),
        header: () => <span>Mask Char</span>,
        size: 100,
      }),
      columnHelper.accessor('maskPattern', {
        cell: info => {
          const pattern = info.getValue();
          const rowData = info.row.original;
          const readablePattern = parseMaskPattern(pattern);
          const example = getMaskExample(
            pattern,
            rowData.maskType,
            rowData.maskChar,
          );

          if (!pattern) return <span className="text-gray-400">-</span>;

          return (
            <Popover
              content={
                <div className="text-sm">
                  <div className="mb-2">
                    <span className="text-gray-500">Pattern: </span>
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                      {pattern}
                    </code>
                  </div>
                  {example && (
                    <div>
                      <span className="text-gray-500">Example: </span>
                      <code className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs">
                        {example}
                      </code>
                    </div>
                  )}
                </div>
              }
              title="Mask Pattern Details"
              trigger="hover"
            >
              <div className="flex items-center gap-1.5 cursor-help">
                <span className="text-gray-700">{readablePattern}</span>
                <Info className="size-3.5 text-gray-400" />
              </div>
            </Popover>
          );
        },
        header: () => <span>Mask Pattern</span>,
        size: 200,
      }),
      columnHelper.accessor('createdByUser', {
        cell: info => {
          const user = info.getValue();
          if (user?.FullName && user?.EmployeeCode) {
            return `${user.FullName} - ${user.EmployeeCode}`;
          }
          return user?.FullName || '-';
        },
        header: () => <span>Created By</span>,
        size: 180,
      }),
      columnHelper.accessor('updatedByUser', {
        cell: info => {
          const user = info.getValue();
          if (user?.FullName && user?.EmployeeCode) {
            return `${user.FullName} - ${user.EmployeeCode}`;
          }
          return user?.FullName || '-';
        },
        header: () => <span>Updated By</span>,
        size: 180,
      }),
      columnHelper.accessor('createdAt', {
        cell: info => (
          <span>
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
              : '-'}
          </span>
        ),
        header: () => <span>Created At</span>,
        size: 180,
      }),
      columnHelper.accessor('updatedAt', {
        cell: info => (
          <span>
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
              : '-'}
          </span>
        ),
        header: () => <span>Updated At</span>,
        size: 180,
      }),
    ],
    [
      handleStatusToggle,
      handleOpenEditSideSheet,
      updateMaskingResponse.isLoading,
    ],
  );

  // React Table instance
  const table = useReactTable({
    data: maskingDataResponse?.data?.data || [],
    columns: columns as unknown as ColumnDef<IDataMaskingTableColumns>[],
    getCoreRowModel: getCoreRowModel(),
    enableColumnPinning: true,
    initialState: {
      columnPinning: {
        left: ['action', 'status', 'tableName'],
      },
    },
  });

  // Side sheet form content
  const sideSheetContent = (
    <div className="p-4">
      <form className="flex flex-col gap-4" onSubmit={e => e.preventDefault()}>
        <SelectInput
          name="tableName"
          control={maskForm.control}
          rules={{ required: '* Table name is required' }}
          label="Table Name *"
          formState={maskForm.formState}
          config={{
            showSearch: true,
            placeholder: 'Select table name',
            options:
              tablesResponse?.data?.map((table: any) => ({
                value: table,
                label: table,
              })) || [],
            allowClear: true,
            optionFilterProp: 'label',
            optionLabelProp: 'label',
          }}
          addOnClass="w-full"
        />

        <SelectInput
          name="columnName"
          control={maskForm.control}
          rules={{ required: '* Column name is required' }}
          label="Column Name *"
          formState={maskForm.formState}
          config={{
            showSearch: true,
            placeholder: 'Select column name',
            disabled: !watchedTableName,
            options:
              tableColumnsResponse?.data?.map((column: any) => ({
                value: column.name,
                label: column.name,
              })) || [],
            allowClear: true,
            optionFilterProp: 'label',
            optionLabelProp: 'label',
          }}
          addOnClass="w-full"
        />

        <SelectInput
          name="maskType"
          control={maskForm.control}
          rules={{ required: '* Mask type is required' }}
          label="Mask Type *"
          formState={maskForm.formState}
          config={{
            placeholder: 'Select mask type',
            options: MASK_TYPE_OPTIONS,
            allowClear: true,
            optionFilterProp: 'label',
            optionLabelProp: 'label',
          }}
          addOnClass="w-full"
        />

        <TextInput
          name="maskChar"
          control={maskForm.control}
          label="Mask Character"
          formState={maskForm.formState}
          config={{
            placeholder: 'Enter mask character (default: *)',
            maxLength: 1,
          }}
        />

        <TextInput
          name="maskPattern"
          control={maskForm.control}
          label="Mask Pattern"
          formState={maskForm.formState}
          config={{
            placeholder: 'e.g., S{2}*E{2}, S{-4}, E{-4}',
          }}
        />

        <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-600">
          <p className="font-semibold mb-1">Pattern Guide:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>
              <code className="bg-gray-200 px-1 rounded">S&#123;n&#125;</code> -
              Show first n characters
            </li>
            <li>
              <code className="bg-gray-200 px-1 rounded">S&#123;-n&#125;</code>{' '}
              - Hide last n characters
            </li>
            <li>
              <code className="bg-gray-200 px-1 rounded">E&#123;n&#125;</code> -
              Show last n characters
            </li>
            <li>
              <code className="bg-gray-200 px-1 rounded">E&#123;-n&#125;</code>{' '}
              - Hide first n characters
            </li>
            <li>
              <code className="bg-gray-200 px-1 rounded">*</code> - Mask middle
              characters
            </li>
            <li>
              <code className="bg-gray-200 px-1 rounded">@domain</code> -
              Preserve email domain
            </li>
          </ul>
        </div>

        <SelectInput
          name="status"
          control={maskForm.control}
          label="Status"
          formState={maskForm.formState}
          config={{
            placeholder: 'Select status',
            options: STATUS_OPTIONS,
            optionFilterProp: 'label',
            optionLabelProp: 'label',
          }}
          addOnClass="w-full"
        />
      </form>
    </div>
  );

  // Main content
  let mainContent = (
    <>
      <CommonTravelTable
        table={table}
        total={totalCount}
        appliedFilters={pagination}
        setAppliedFilters={setPagination}
      />

      <AnimatePresence>
        {isSideSheetOpen && (
          <SideSheet
            isOpen={isSideSheetOpen}
            onClose={handleCloseSideSheet}
            addOnClass="w-[500px]"
            contentChildren={sideSheetContent}
            headerChildren={
              <div className="border-b border-gray-100">
                <SideSheetHeader
                  title={
                    isEditMode ? 'Edit Configuration' : 'Add Configuration'
                  }
                  onCloseClick={handleCloseSideSheet}
                />
              </div>
            }
            footerChildren={
              <div className="p-4 border-t">
                <div className="flex items-center gap-4">
                  <ButtonWizard
                    addOnClass="bg-primary-600"
                    onBtnClick={maskForm.handleSubmit(handleFormSubmit)}
                    buttonText={isEditMode ? 'Update' : 'Create'}
                    loading={
                      isEditMode
                        ? updateMaskingResponse.isLoading
                        : createMaskingResponse.isLoading
                    }
                  />
                  <ButtonWizard
                    addOnClass="bg-white border border-gray-300"
                    addOnTextClass="text-gray-700"
                    onBtnClick={handleCloseSideSheet}
                    buttonText="Cancel"
                  />
                </div>
              </div>
            }
          />
        )}
      </AnimatePresence>
    </>
  );

  if (maskingDataResponse.isFetching && !maskingDataResponse.data) {
    mainContent = (
      <div className="p-4">
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div className="p-1.5 flex flex-col gap-2">
      <CardWizard addOnClass="p-0 pt-2.5">
        <div className="flex items-center justify-between px-4 mb-3">
          <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm flex items-center gap-x-2">
            <span>Secure Masking Configurations</span>
          </HeadingWizard>
          <div className="flex items-center justify-end gap-2">
            <Input
              name="search"
              value={searchText}
              allowClear
              type="text"
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by table name"
              className="w-56"
            />
            <ButtonWizard
              addOnClass="bg-primary-600"
              addOnTextClass=""
              buttonText="Add Configuration"
              onBtnClick={handleOpenAddSideSheet}
            >
              <PlusIcon className="size-4 stroke-[2px] font-semibold text-white" />
            </ButtonWizard>
            <span
              onClick={() => navigate('/settings/mappings')}
              className={
                'cursor-pointer text-xs px-2 py-1 rounded-md text-[#4649EC] font-semibold hover:opacity-80 hover:underline transition-colors flex items-center gap-1 w-max border border-[#4649EC]'
              }
            >
              <span>Back</span>
              <ArrowRightIcon className="size-3 stroke-[4px]  text-[#4649EC]" />
            </span>
          </div>
        </div>
        {mainContent}
      </CardWizard>
    </div>
  );
};

export default SecureMaskingDashboard;
